// ¿Ya hizo el reto de HOY? Lo necesita la card protagonista del estudiante
// (caso 3 del plan de la interfaz de los tres mundos, §2.2): con el reto
// hecho, la card deja de ofrecerlo y ofrece "Arma un PAE".
//
// Decisión del fundador 2026-10-04: el dato SIGUE A LA CUENTA (si hizo el
// reto en el teléfono, el computador también lo sabe). La cuenta ya lo tiene:
// el evento `reto_dia_completado` de `eventos_v2`, que la persona puede leer
// de sí misma (RLS `eventos_v2_user_read_own`) con el índice (user_id,
// created_at). No se creó tabla ni columna nueva. Es dato de la racha de la
// persona, jamás de un paciente.
//
// Además hay un ESPEJO local por dispositivo, por dos razones: (1) el evento
// puede ir en la cola offline de eventTracking y no estar aún en la BD al
// volver al inicio, y (2) así la card cambia al instante, sin esperar la red.
// El espejo solo puede AFIRMAR "ya lo hizo"; nunca niega lo que diga la cuenta.
//
// Archivo aparte de lib/retoDelDia.js a propósito: ese es puro (selección de
// preguntas) y no debe arrastrar el cliente de Supabase.

import { supabase } from './supabase';
import { fechaKey } from './turnoRacha';

const CLAVE = (userId) => `mipae:reto:hecho:${userId || 'anon'}`;

// Marca local: la fecha (YYYY-MM-DD local) del último reto terminado y, si se
// conocen, las respuestas que dio (id de pregunta → opción elegida). Las
// respuestas permiten mostrar el resultado al volver y repasar solo las que
// fallaron; son texto del banco de MiPAE, nunca algo escrito por la persona.
export function marcarRetoHecho(userId, fecha = fechaKey(), respuestas = null) {
  const valor = { fecha };
  if (respuestas && typeof respuestas === 'object') valor.respuestas = respuestas;
  try { localStorage.setItem(CLAVE(userId), JSON.stringify(valor)); } catch { /* sin storage: queda la cuenta */ }
}

function leerMarca(userId) {
  try {
    const crudo = localStorage.getItem(CLAVE(userId));
    if (!crudo) return null;
    const v = JSON.parse(crudo);
    return v && typeof v === 'object' && typeof v.fecha === 'string' ? v : null;
  } catch {
    return null;
  }
}

export function retoHechoLocal(userId, fecha = fechaKey()) {
  return leerMarca(userId)?.fecha === fecha;
}

// Las respuestas de HOY en este aparato, o null si no hay. Re-validadas al
// leer: solo pares texto → texto (un storage adulterado no rompe la pantalla).
export function respuestasDeHoy(userId, fecha = fechaKey()) {
  const m = leerMarca(userId);
  if (!m || m.fecha !== fecha || !m.respuestas || typeof m.respuestas !== 'object') return null;
  const limpias = {};
  for (const [k, v] of Object.entries(m.respuestas)) {
    if (typeof k === 'string' && typeof v === 'string') limpias[k] = v;
  }
  return limpias;
}

// Inicio del día LOCAL en ISO, para comparar contra created_at (timestamptz).
// "Hoy" es el día de quien mira, el mismo de fechaKey() y de la racha.
export function inicioDelDia(d = new Date()) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).toISOString();
}

// Lee la cuenta: el resultado del reto de hoy ({aciertos, total}) o null.
// Fail-safe hacia "no lo sé" = null: en el peor caso la card sigue ofreciendo
// el reto, que es lo que hacía antes. Los números se re-validan al leer.
export async function retoDeHoyEnCuenta(userId, ahora = new Date()) {
  if (!userId) return null;
  try {
    const { data, error } = await supabase
      .from('eventos_v2')
      .select('payload')
      .eq('user_id', userId)
      .eq('evento', 'reto_dia_completado')
      .gte('created_at', inicioDelDia(ahora))
      .limit(1);
    if (error || !Array.isArray(data) || data.length === 0) return null;
    const p = data[0]?.payload || {};
    const entero = (n) => (Number.isInteger(n) && n >= 0 ? n : null);
    const total = entero(p.total);
    const aciertos = entero(p.aciertos);
    return { aciertos: total != null && aciertos != null && aciertos <= total ? aciertos : null, total };
  } catch {
    return null;
  }
}

export async function retoHechoEnCuenta(userId, ahora = new Date()) {
  return (await retoDeHoyEnCuenta(userId, ahora)) != null;
}

// Local primero (instantáneo); si no, la cuenta, y si la cuenta dice que sí
// se espeja para la próxima vez.
export async function retoHechoHoy(userId, ahora = new Date()) {
  const fecha = fechaKey(ahora);
  if (retoHechoLocal(userId, fecha)) return true;
  const enCuenta = await retoHechoEnCuenta(userId, ahora);
  // Se re-lee antes de escribir: si mientras esperaba la persona terminó el
  // reto aquí, su marca (con respuestas) no se pisa con una sin ellas.
  if (enCuenta && !retoHechoLocal(userId, fecha)) marcarRetoHecho(userId, fecha);
  return enCuenta;
}
